import { useAppRouteParams } from "@/AppRoutes";
import { postCopySheet } from "@/api/sheets";
import {
  BulkActionRequest,
  useBulkActionToast,
} from "@/components/AppProvider/AppNotificationProvider/useBulkActionToast";
import {
  createNewDisplayNameOrThrow,
  createNewNameOrThrow,
} from "@/helpers/createNewName";
import React, { useCallback, useEffect, useRef } from "react";
import { Sheet } from "../../../../../domain/Sheet";

/**
 * 2件以上のシートを即時複製し、Toast で進捗・結果を通知するフック。
 * BulkActionRequest.phase が "requested" かつ itemIds.length > 1 のときに実行する。
 * APIの /sheets/{sheet_id}/copy エンドポイントを使用してシートを複製する。
 */
export const useSelectedSheetsDuplicateAction = ({
  sheets,
  duplicateRequest,
  setDuplicateRequest,
  setSelectedSheetIds,
}: {
  sheets: Sheet[];
  duplicateRequest: BulkActionRequest;
  setDuplicateRequest: React.Dispatch<React.SetStateAction<BulkActionRequest>>;
  setSelectedSheetIds: React.Dispatch<React.SetStateAction<string[]>>;
}) => {
  const { organizationId, versionId } = useAppRouteParams();

  // NOTE: 名前の重複回避のため、処理中に更新される配列をrefで管理
  const displayNamesRef = useRef<string[]>([]);
  const namesRef = useRef<string[]>([]);

  const { runWithToast } = useBulkActionToast({
    inProgress: (count) =>
      count === 1 ? "シートを複製中..." : `${count}件のシートを複製中...`,
    success: (count) =>
      count === 1 ? "シートを複製しました" : `${count}件のシートを複製しました`,
    error: (count) =>
      count === 1
        ? "シートを複製できませんでした"
        : `${count}件のシートを複製できませんでした`,
  });

  const runDuplicate = useCallback(
    async (sheet: Sheet) => {
      const newDisplayName = createNewDisplayNameOrThrow(
        displayNamesRef.current,
        sheet.displayName
      );
      const newName = createNewNameOrThrow(namesRef.current, sheet.name);

      // APIを使用してシートを複製
      await postCopySheet({
        organizationId,
        versionId,
        sheetId: sheet.id,
        name: newName,
        displayName: newDisplayName,
      });

      displayNamesRef.current.push(newDisplayName);
      namesRef.current.push(newName);
    },
    [organizationId, versionId]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (
      duplicateRequest.phase !== "requested" ||
      duplicateRequest.itemIds.length <= 1
    )
      return;

    const targetSheets = sheets.filter((s) =>
      duplicateRequest.itemIds.includes(s.id)
    );
    if (targetSheets.length === 0) {
      setDuplicateRequest({ phase: "idle", itemIds: [] });
      return;
    }

    // 即時 in_progress へ遷移してリマウント時の二重実行を防ぐ
    setDuplicateRequest({
      phase: "in_progress",
      itemIds: duplicateRequest.itemIds,
    });

    const run = async () => {
      displayNamesRef.current = sheets.map((s) => s.displayName);
      namesRef.current = sheets.map((s) => s.name);

      const { failedIds } = await runWithToast(targetSheets, runDuplicate);
      setSelectedSheetIds(failedIds);
      setDuplicateRequest({ phase: "idle", itemIds: [] });
    };

    run();
  }, [duplicateRequest.phase]);
};
